// Astrocartografia, 3 modes: specific city, current GPS location, global ranking
// Route: /astrocartografia

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { safeInvoke } from '../lib/safeInvoke';
import { supabase } from '../lib/supabase';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { EmptyState } from '../components/ui/EmptyState';
import { scoreColor, shareAtlasCustomCity } from '../lib/atlasHelpers';

// Supabase edge function proxy for Nominatim, bypasses browser CSP restriction.
const NOMINATIM_PROXY = `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/nominatim-proxy`;

// ─── Types ───────────────────────────────────────────────────────────────────

type Mode = 'specific' | 'current' | 'global';

interface Dimension {
  key:            string;
  label:          string;
  icon:           string;
  score:          number;
  planetaryLines: string;
  analysis:       string;
  tip:            string;
}

interface AnalysisResult {
  id?:                 string;
  locationName:        string;
  flag:                string;
  country:             string;
  lat?:                number;
  lon?:                number;
  overallScore:        number;
  overallSummary:      string;
  planetaryHighlights: string;
  dimensions:          Dimension[];
  topStrengths:        string[];
  topChallenges:       string[];
  analysisType?:       Mode;
}

interface GlobalCity {
  locationName:   string;
  country:        string;
  flag:           string;
  overallScore:   number;
  careerScore:    number;
  moneyScore:     number;
  successScore:   number;
  loveScore:      number;
  socialScore:    number;
  familyScore:    number;
  healthScore:    number;
  spiritualScore: number;
  adventureScore: number;
  keyAspect:      string;
}

interface LocationSuggestion {
  key:      string;
  label:    string;
  cityName: string;
  country:  string;
  flag?:    string;
  lat:      string;
  lon:      string;
}

interface NominatimResult {
  place_id:     number;
  display_name: string;
  lat:          string;
  lon:          string;
  type:         string;
  class:        string;
  address: {
    city?:         string;
    town?:         string;
    village?:      string;
    municipality?: string;
    county?:       string;
    state?:        string;
    country?:      string;
    country_code?: string;
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const COUNTRY_FLAG: Record<string, string> = {
  br:'🇧🇷', us:'🇺🇸', gb:'🇬🇧', fr:'🇫🇷', de:'🇩🇪', jp:'🇯🇵', cn:'🇨🇳',
  pt:'🇵🇹', es:'🇪🇸', it:'🇮🇹', ca:'🇨🇦', au:'🇦🇺', ar:'🇦🇷', mx:'🇲🇽',
  in:'🇮🇳', ru:'🇷🇺', za:'🇿🇦', ng:'🇳🇬', eg:'🇪🇬', nl:'🇳🇱', be:'🇧🇪',
  ch:'🇨🇭', se:'🇸🇪', no:'🇳🇴', dk:'🇩🇰', fi:'🇫🇮', pl:'🇵🇱', at:'🇦🇹',
  tr:'🇹🇷', kr:'🇰🇷', sg:'🇸🇬', ae:'🇦🇪', sa:'🇸🇦', il:'🇮🇱', nz:'🇳🇿',
  cl:'🇨🇱', co:'🇨🇴', pe:'🇵🇪', uy:'🇺🇾', th:'🇹🇭', id:'🇮🇩', hk:'🇭🇰',
  cz:'🇨🇿', ke:'🇰🇪',
};

const ALLOWED_TYPES = new Set([
  'city','town','village','hamlet','suburb','borough',
  'municipality','county','state','country','region','quarter',
  'administrative','locality','district','island','archipelago',
]);

const LOADING_PHASES = [
  'Localizando nas efemérides...',
  'Calculando linhas planetárias...',
  'Analisando Ascendente e MC locais...',
  'Verificando aspectos com planetas natais...',
  'Calculando 9 dimensões de vida...',
  'Preparando sua análise personalizada...',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Resolve city text → lat/lon via Nominatim (used by Analisar button click) */
async function resolveLocationCoords(q: string): Promise<{ cityName: string; lat: number; lon: number } | null> {
  try {
    const url = `${NOMINATIM_PROXY}?mode=search&q=${encodeURIComponent(q)}&limit=1`;
    const res = await fetch(url);
    const data: NominatimResult[] = await res.json();
    if (!data.length) return null;
    const s = nominatimToSuggestion(data[0]);
    if (!s) return null;
    return { cityName: s.cityName, lat: parseFloat(s.lat), lon: parseFloat(s.lon) };
  } catch {
    return null;
  }
}

function nominatimToSuggestion(r: NominatimResult): LocationSuggestion | null {
  const addr = r.address;
  const local = addr.city ?? addr.town ?? addr.municipality ?? addr.village ?? addr.county;
  const parts: string[] = [];
  if (local) parts.push(local);
  if (addr.state && addr.state !== local) parts.push(addr.state);
  if (addr.country) parts.push(addr.country);
  if (parts.length === 0) return null;
  const label = parts.join(', ');
  const cc = (addr.country_code ?? '').toLowerCase();
  return {
    key:     `${r.place_id}`,
    label,
    cityName: label,
    country:  addr.country ?? '',
    flag:     COUNTRY_FLAG[cc] ?? '🌐',
    lat:      r.lat,
    lon:      r.lon,
  };
}

function sanitizeError(raw: string | null | undefined): string {
  if (!raw) return 'Erro ao processar. Tente novamente.';
  const lower = raw.toLowerCase();
  if (lower.includes('credit balance') || lower.includes('billing') || lower.includes('quota'))
    return 'Serviço temporariamente indisponível. Tente novamente em alguns instantes.';
  if (lower.includes('timeout') || lower.includes('timed out'))
    return 'A análise demorou mais que o esperado. Tente novamente.';
  if (lower.includes('birth_date') || lower.includes('mapa natal'))
    return 'Complete seu perfil com data de nascimento para usar a Astrocartografia.';
  // Hide raw JSON / stack traces from users
  if (raw.includes('{') || raw.includes('Error:') || raw.length > 120)
    return 'Erro ao processar a análise. Tente novamente.';
  return raw;
}

function scoreBg(score: number): string {
  if (score >= 85) return '#22c55e';
  if (score >= 70) return '#C4A951';
  if (score >= 55) return '#f59e0b';
  return '#ef4444';
}

// ─── LoadingScreen ────────────────────────────────────────────────────────────

function LoadingScreen({ locationName }: { locationName: string }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const total = LOADING_PHASES.length;
    const interval = setInterval(() => {
      setPhaseIdx(i => {
        const next = i < total - 1 ? i + 1 : i;
        setProgress(Math.round(((next + 1) / total) * 100));
        return next;
      });
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ padding: '2rem 1rem', maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
      <span style={{ fontSize: '3rem', display: 'inline-block', animation: 'pulse 2s infinite' }}>🌍</span>
      <h2 style={{ color: 'var(--petrol)', marginTop: '0.5rem', fontFamily: 'var(--font-display, serif)' }}>
        Analisando {locationName}
      </h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', minHeight: '1.4em' }}>
        {LOADING_PHASES[phaseIdx]}
      </p>
      <div style={{
        margin: '1.25rem auto 0',
        height: 4,
        background: 'rgba(255,255,255,0.08)',
        borderRadius: 4,
        overflow: 'hidden',
        maxWidth: 320,
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          background: 'var(--petrol)',
          borderRadius: 4,
          transition: 'width 0.6s ease',
        }} />
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.72rem', marginTop: '0.5rem' }}>{progress}%</p>
    </div>
  );
}

// ─── ModeSelector ─────────────────────────────────────────────────────────────

const MODES: { id: Mode; icon: string; title: string; desc: string }[] = [
  { id: 'specific', icon: '🔍', title: 'Localização Específica', desc: 'Analise qualquer cidade, região ou país' },
  { id: 'current',  icon: '📍', title: 'Minha Localização',      desc: 'Use o GPS para analisar onde você está' },
  { id: 'global',   icon: '🌐', title: 'Ranking Global',          desc: 'Compare ~30 cidades do mundo para você' },
];

function ModeSelector({ active, onChange }: { active: Mode; onChange: (m: Mode) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '0.6rem', marginBottom: '1.5rem' }}>
      {MODES.map(m => (
        <button
          key={m.id}
          onClick={() => onChange(m.id)}
          style={{
            padding: '0.75rem 0.5rem',
            borderRadius: 'var(--radius-md)',
            border: active === m.id ? '2px solid var(--petrol)' : '1px solid var(--border-default)',
            background: active === m.id ? 'rgba(31,78,95,0.08)' : 'var(--bg-elevated)',
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'border 0.15s',
          }}
        >
          <div style={{ fontSize: '1.4rem' }}>{m.icon}</div>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: active === m.id ? 'var(--petrol)' : 'var(--text-primary)', marginTop: '0.25rem', lineHeight: 1.3 }}>
            {m.title}
          </div>
          <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.2rem', lineHeight: 1.3 }}>
            {m.desc}
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── DimensionCard ────────────────────────────────────────────────────────────

function DimensionCard({ dim }: { dim: Dimension }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{
      border: '1px solid var(--border-default)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
      marginBottom: '0.5rem',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem',
          padding: '0.75rem 1rem',
          background: 'var(--bg-elevated)',
          border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{dim.icon}</span>
        <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          {dim.label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <div style={{ width: 60, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${dim.score}%`, height: '100%', background: scoreBg(dim.score), borderRadius: 3 }} />
          </div>
          <span className={scoreColor(dim.score)} style={{ fontSize: '0.8rem', fontWeight: 700, width: 28, textAlign: 'right' }}>
            {dim.score}
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{open ? '▲' : '▼'}</span>
        </div>
      </button>
      {open && (
        <div style={{ padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.15)' }}>
          {dim.planetaryLines && (
            <p style={{ fontSize: '0.75rem', color: 'var(--petrol)', marginBottom: '0.5rem', fontStyle: 'italic' }}>
              🪐 {dim.planetaryLines}
            </p>
          )}
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.65, marginBottom: '0.5rem' }}>
            {dim.analysis}
          </p>
          {dim.tip && (
            <p style={{ fontSize: '0.75rem', color: 'var(--color-success)', lineHeight: 1.55 }}>
              ✦ {dim.tip}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── AnalysisResultView ───────────────────────────────────────────────────────

function AnalysisResultView({
  result,
  onBack,
  onShare,
}: {
  result: AnalysisResult;
  onBack: () => void;
  onShare: () => void;
}) {
  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '1rem' }}>
      <button className="btn btn-secondary btn-sm" style={{ marginBottom: '1rem' }} onClick={onBack}>
        ← Nova análise
      </button>

      {/* Header card */}
      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '2.75rem', lineHeight: 1 }}>{result.flag}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, color: 'var(--text-primary)', fontFamily: 'var(--font-display, serif)', fontSize: '1.2rem' }}>
              {result.locationName}
            </h2>
            <p style={{ margin: '0.1rem 0 0', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{result.country}</p>
          </div>
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <div className={scoreColor(result.overallScore)} style={{ fontSize: '2.25rem', fontWeight: 700, lineHeight: 1 }}>
              {result.overallScore}
            </div>
            <div style={{ fontSize: '0.6rem', color: 'var(--text-secondary)' }}>/100</div>
          </div>
        </div>

        {/* Score bar */}
        <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden', marginBottom: '1rem' }}>
          <div style={{ width: `${result.overallScore}%`, height: '100%', background: scoreBg(result.overallScore), borderRadius: 4, transition: 'width 1s ease' }} />
        </div>

        {/* Overall summary */}
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.65, marginBottom: '0.75rem' }}>
          {result.overallSummary}
        </p>

        {/* Planetary highlights */}
        {result.planetaryHighlights && (
          <p style={{ fontSize: '0.8rem', color: 'var(--petrol)', fontStyle: 'italic', marginBottom: '0.75rem' }}>
            🪐 {result.planetaryHighlights}
          </p>
        )}

        {/* Strengths + Challenges */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          {result.topStrengths?.length > 0 && (
            <div>
              <p style={{ fontSize: '0.72rem', color: 'var(--color-success)', fontWeight: 600, marginBottom: '0.3rem' }}>✦ Forças</p>
              {result.topStrengths.map(s => (
                <p key={s} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.1rem 0' }}>· {result.dimensions?.find(d => d.key === s)?.label ?? s}</p>
              ))}
            </div>
          )}
          {result.topChallenges?.length > 0 && (
            <div>
              <p style={{ fontSize: '0.72rem', color: 'var(--color-error)', fontWeight: 600, marginBottom: '0.3rem' }}>⚠ Desafios</p>
              {result.topChallenges.map(c => (
                <p key={c} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.1rem 0' }}>· {result.dimensions?.find(d => d.key === c)?.label ?? c}</p>
              ))}
            </div>
          )}
        </div>

        <button className="btn-secondary" style={{ width: '100%' }} onClick={onShare}>
          📲 Compartilhar no WhatsApp
        </button>
      </div>

      {/* 9 dimensions */}
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
        9 Dimensões de Vida
      </p>
      {result.dimensions?.map(d => <DimensionCard key={d.key} dim={d} />)}
    </div>
  );
}

// ─── GlobalRankingView ────────────────────────────────────────────────────────

const DIM_KEYS: { key: keyof GlobalCity; icon: string; short: string }[] = [
  { key: 'careerScore',    icon: '💼', short: 'Trab.' },
  { key: 'moneyScore',     icon: '💰', short: 'Din.' },
  { key: 'successScore',   icon: '🏆', short: 'Suc.' },
  { key: 'loveScore',      icon: '💕', short: 'Amor' },
  { key: 'socialScore',    icon: '🤝', short: 'Soc.' },
  { key: 'healthScore',    icon: '🌿', short: 'Saúde' },
  { key: 'spiritualScore', icon: '✨', short: 'Esp.' },
  { key: 'adventureScore', icon: '🌏', short: 'Av.' },
];

function GlobalRankingView({
  cities,
  onBack,
}: {
  cities: GlobalCity[];
  onBack: () => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '1rem' }}>
      <button className="btn btn-secondary btn-sm" style={{ marginBottom: '1rem' }} onClick={onBack}>
        ← Nova análise
      </button>

      <h2 style={{ color: 'var(--petrol)', fontFamily: 'var(--font-display, serif)', marginBottom: '0.25rem' }}>
        🌐 Ranking Global
      </h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: '1rem' }}>
        {cities.length} cidades do mundo ranqueadas para o seu mapa natal. Toque em uma cidade para ver os detalhes.
      </p>

      {cities.map((city, idx) => (
        <div
          key={city.locationName}
          style={{
            marginBottom: '0.5rem',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          <button
            onClick={() => setExpanded(e => e === city.locationName ? null : city.locationName)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              padding: '0.75rem 1rem',
              background: 'var(--bg-elevated)',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span style={{
              fontSize: '0.7rem', fontWeight: 700,
              color: idx < 3 ? '#C4A951' : 'var(--text-muted)',
              width: 18, flexShrink: 0, textAlign: 'center',
            }}>
              {idx + 1}
            </span>
            <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>{city.flag}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                {city.locationName}
              </p>
              {city.keyAspect && (
                <p style={{ margin: 0, fontSize: '0.68rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  🪐 {city.keyAspect}
                </p>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
              <div style={{ width: 50, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${city.overallScore}%`, height: '100%', background: scoreBg(city.overallScore), borderRadius: 3 }} />
              </div>
              <span className={scoreColor(city.overallScore)} style={{ fontSize: '0.85rem', fontWeight: 700, width: 26, textAlign: 'right' }}>
                {city.overallScore}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                {expanded === city.locationName ? '▲' : '▼'}
              </span>
            </div>
          </button>

          {expanded === city.locationName && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.15)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {DIM_KEYS.map(d => (
                  <div key={d.key} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    minWidth: 50, padding: '0.4rem 0.5rem',
                    background: 'rgba(255,255,255,0.04)', borderRadius: 8,
                  }}>
                    <span style={{ fontSize: '0.9rem' }}>{d.icon}</span>
                    <span className={scoreColor(city[d.key] as number)} style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                      {city[d.key] as number}
                    </span>
                    <span style={{ fontSize: '0.58rem', color: 'var(--text-muted)' }}>{d.short}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function Astrocartografia() {
  useSystemUsageTracker('astrocartografia');
  const navigate = useNavigate();
  const { profile, user } = useAuth();

  const [mode, setMode]               = useState<Mode>('specific');
  const [query, setQuery]             = useState('');
  const [isLoading, setIsLoading]     = useState(false);
  const [resolving, setResolving]     = useState(false);
  const [loadingName, setLoadingName] = useState('');
  const [error, setError]             = useState<string | null>(null);
  const [result, setResult]           = useState<AnalysisResult | null>(null);
  const [globalCities, setGlobalCities] = useState<GlobalCity[] | null>(null);
  const [history, setHistory]         = useState<AnalysisResult[]>([]);
  const [gpsStatus, setGpsStatus]     = useState<'idle' | 'locating' | 'done' | 'error'>('idle');

  // Autocomplete
  const [suggestions, setSuggestions]       = useState<LocationSuggestion[]>([]);
  const [showDropdown, setShowDropdown]     = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [activeIdx, setActiveIdx]           = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef  = useRef<HTMLDivElement>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function handleDeleteAnalysis(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    if (!user?.id || deletingId) return;
    setDeletingId(id);
    try {
      await supabase
        .from('astrocartografia_analyses')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', user.id);
      setHistory(prev => prev.filter(h => h.id !== id));
    } finally {
      setDeletingId(null);
    }
  }

  // Load history (exclude soft-deleted entries)
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('astrocartografia_analyses')
      .select('id,location_name,country,flag,overall_score,overall_summary,planetary_highlights,dimensions,top_strengths,top_challenges,lat,lon')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10)
      .then(({ data }) => {
        if (data) {
          setHistory(data.map(r => ({
            id:                 r.id as string,
            locationName:       r.location_name as string,
            country:            r.country as string,
            flag:               r.flag as string,
            overallScore:       r.overall_score as number,
            overallSummary:     r.overall_summary as string,
            planetaryHighlights: r.planetary_highlights as string,
            dimensions:         (r.dimensions ?? []) as Dimension[],
            topStrengths:       (r.top_strengths ?? []) as string[],
            topChallenges:      (r.top_challenges ?? []) as string[],
            lat:                r.lat as number | undefined,
            lon:                r.lon as number | undefined,
          })));
        }
      });
  }, [user?.id]);

  // Close dropdown on outside click/tap (pointerdown covers mouse + touch)
  useEffect(() => {
    const handler = (e: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node))
        setShowDropdown(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, []);

  useEffect(() => { setActiveIdx(-1); }, [suggestions]);

  // Nominatim autocomplete
  const fetchSuggestions = useCallback((q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 3) { setSuggestions([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSuggestLoading(true);
      try {
        const url = `${NOMINATIM_PROXY}?mode=search&q=${encodeURIComponent(q)}&limit=10`;
        const res = await fetch(url);
        const data: NominatimResult[] = await res.json();
        const seen = new Set<string>();
        const items: LocationSuggestion[] = [];
        for (const r of data) {
          if (!ALLOWED_TYPES.has(r.type) && r.class !== 'boundary') continue;
          const s = nominatimToSuggestion(r);
          if (!s || seen.has(s.label)) continue;
          seen.add(s.label);
          items.push(s);
          if (items.length >= 7) break;
        }
        setSuggestions(items);
        setShowDropdown(items.length > 0);
      } catch { setSuggestions([]); setShowDropdown(false); }
      finally { setSuggestLoading(false); }
    }, 350);
  }, []);

  // Invoke edge function for specific/current
  const analyze = useCallback(async (
    locationName: string,
    lat?: number,
    lon?: number,
    type: Mode = 'specific',
  ) => {
    const loc = locationName.trim();
    if (!loc) return;
    setError(null); setResult(null); setGlobalCities(null);
    setShowDropdown(false); setLoadingName(loc.split(',')[0]); setIsLoading(true);

    const body: Record<string, unknown> = { analysisType: type, location: loc };
    if (lat !== undefined) body.latitude  = lat;
    if (lon !== undefined) body.longitude = lon;

    const { data, fromFallback, error: invErr } = await safeInvoke<AnalysisResult | null>(
      'astrocartografia',
      { body, fallback: null, timeout: 90_000 },
    );

    setIsLoading(false);

    if (!fromFallback && data && !('globalRanking' in (data as object))) {
      const typed = data as AnalysisResult;
      setResult(typed);
      setHistory(prev => [typed, ...prev.filter(h => h.locationName !== typed.locationName).slice(0, 9)]);
    } else {
      setError(sanitizeError(invErr));
    }
  }, []);

  // GPS current location
  const handleCurrentLocation = () => {
    if (!navigator.geolocation) { setError('Geolocalização não suportada neste dispositivo.'); return; }
    setGpsStatus('locating'); setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lon } = pos.coords;
        setGpsStatus('done');
        try {
          const url = `${NOMINATIM_PROXY}?mode=reverse&lat=${lat}&lon=${lon}`;
          const res = await fetch(url);
          const data = await res.json() as NominatimResult;
          const addr = data.address;
          const city = addr.city ?? addr.town ?? addr.municipality ?? addr.village ?? addr.county ?? 'Localização atual';
          const country = addr.country ?? '';
          const locationName = country ? `${city}, ${country}` : city;
          setQuery(locationName);
          await analyze(locationName, lat, lon, 'current');
        } catch {
          setError('Erro ao obter endereço. Tente novamente.');
          setGpsStatus('error');
        }
      },
      (err) => {
        setGpsStatus('error');
        if (err.code === 1) {
          setError('GPS_DENIED');
        } else {
          setError('Erro ao obter localização. Tente novamente.');
        }
      },
      { timeout: 15_000 },
    );
  };

  // Global ranking
  const handleGlobalRanking = async () => {
    setError(null); setResult(null); setGlobalCities(null);
    setLoadingName('o mundo'); setIsLoading(true);

    const { data, fromFallback, error: invErr } = await safeInvoke<{ globalRanking: GlobalCity[] } | null>(
      'astrocartografia',
      { body: { analysisType: 'global' }, fallback: null, timeout: 120_000 },
    );

    setIsLoading(false);

    if (!fromFallback && data && 'globalRanking' in data && Array.isArray(data.globalRanking)) {
      setGlobalCities(data.globalRanking);
    } else {
      setError(sanitizeError(invErr));
    }
  };

  const handleSelect = (s: LocationSuggestion) => {
    setQuery(s.label); setSuggestions([]); setShowDropdown(false);
    analyze(s.cityName, parseFloat(s.lat), parseFloat(s.lon));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || suggestions.length === 0) {
      if (e.key === 'Enter') analyze(query);
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (activeIdx >= 0) handleSelect(suggestions[activeIdx]); else analyze(query); }
    else if (e.key === 'Escape') setShowDropdown(false);
  };

  // ── Guard ──
  if (profile && !profile.birth_date) {
    return (
      <div style={{ padding: '2rem', maxWidth: 600, margin: '0 auto' }}>
        <EmptyState
          emoji="🌍"
          title="Complete seu Perfil Primeiro"
          subtitle="Precisamos da sua data de nascimento para calcular sua Astrocartografia personalizada."
          cta="Completar Perfil"
          ctaAction={() => navigate('/profile')}
        />
      </div>
    );
  }

  // ── Loading ──
  if (isLoading) return <LoadingScreen locationName={loadingName} />;

  // ── Results ──
  if (result) return (
    <AnalysisResultView
      result={result}
      onBack={() => { setResult(null); setError(null); }}
      onShare={() => shareAtlasCustomCity({
        city_confirmed: result.locationName,
        country: result.country,
        flag: result.flag,
        overall_score: result.overallScore,
        verdict: 'Boa',
      })}
    />
  );

  if (globalCities) return (
    <GlobalRankingView
      cities={globalCities}
      onBack={() => { setGlobalCities(null); setError(null); }}
    />
  );

  // ── Search screen ──
  return (
    <div style={{ padding: '1.5rem 1rem', maxWidth: 600, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
        <span style={{ fontSize: '2.5rem' }}>🌍</span>
        <h1 style={{
          color: 'var(--petrol)',
          fontFamily: 'var(--font-display, serif)',
          margin: '0.5rem 0 0.25rem',
          fontSize: 'clamp(1.375rem,4vw,1.75rem)',
        }}>
          Astrocartografia
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
          Descubra como seu mapa natal ressoa com qualquer lugar do mundo. ✦
        </p>
      </div>

      {/* Mode selector */}
      <ModeSelector active={mode} onChange={m => { setMode(m); setError(null); }} />

      {/* Mode: specific, autocomplete search */}
      {mode === 'specific' && (
        <div ref={wrapperRef} style={{ position: 'relative', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                value={query}
                onChange={e => { setQuery(e.target.value); fetchSuggestions(e.target.value); }}
                onKeyDown={handleKeyDown}
                onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                placeholder="Ex: Lisboa, Tóquio, Buenos Aires..."
                autoComplete="off"
                inputMode="search"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '0.625rem 0.875rem',
                  borderRadius: 'var(--radius-md)',
                  borderBottomLeftRadius: showDropdown ? 0 : undefined,
                  borderBottomRightRadius: showDropdown ? 0 : undefined,
                  border: '1px solid var(--border-gold)',
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  fontSize: '0.9rem', outline: 'none',
                }}
              />
              {suggestLoading && (
                <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--petrol)', fontSize: '0.75rem', animation: 'pulse-skeleton 1s ease-in-out infinite' }}>
                  Buscando...
                </span>
              )}
            </div>
            <button
              className="btn-primary"
              onClick={async () => {
                const q = query.trim();
                if (!q) return;
                setShowDropdown(false);
                // If there's already a selected suggestion, analyze directly
                // Otherwise resolve coords via Nominatim first for accuracy
                setResolving(true);
                const resolved = await resolveLocationCoords(q);
                setResolving(false);
                if (resolved) {
                  analyze(resolved.cityName, resolved.lat, resolved.lon);
                } else {
                  analyze(q);
                }
              }}
              disabled={!query.trim() || resolving}
              style={{ whiteSpace: 'nowrap', padding: '0.625rem 1.25rem', minWidth: '100px' }}
            >
              {resolving ? '🔍...' : 'Analisar'}
            </button>
          </div>

          {showDropdown && suggestions.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-gold)', borderTop: 'none',
              borderRadius: '0 0 var(--radius-md) var(--radius-md)',
              zIndex: 200, overflow: 'auto',
              maxHeight: '260px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.65)',
              WebkitOverflowScrolling: 'touch',
            }}>
              {suggestions.map((s, i) => {
                // Split label into city and region+country for clear disambiguation
                const parts = s.label.split(', ');
                const primary = parts[0];
                const secondary = parts.slice(1).join(', ');
                return (
                  <button
                    key={s.key}
                    onPointerDown={e => { e.preventDefault(); handleSelect(s); }}
                    onMouseEnter={() => setActiveIdx(i)}
                    onMouseLeave={() => setActiveIdx(-1)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.6rem',
                      width: '100%', padding: '0.65rem 0.875rem',
                      background: i === activeIdx ? 'rgba(31,78,95,0.12)' : 'transparent',
                      border: 'none',
                      borderBottom: i < suggestions.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{s.flag}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', color: 'var(--text-primary)', fontSize: '0.875rem', fontWeight: 500 }}>
                        {primary}
                      </span>
                      {secondary && (
                        <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.72rem', marginTop: '1px' }}>
                          {secondary}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Mode: current, GPS */}
      {mode === 'current' && (
        <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
          <button
            className="btn-primary"
            onClick={handleCurrentLocation}
            disabled={gpsStatus === 'locating'}
            style={{ padding: '0.75rem 2rem', fontSize: '0.9rem' }}
          >
            {gpsStatus === 'locating' ? '📍 Obtendo localização...' : '📍 Usar minha localização atual'}
          </button>
          {gpsStatus === 'done' && query && (
            <p style={{ color: 'var(--petrol)', fontSize: '0.8rem', marginTop: '0.5rem' }}>✓ {query}</p>
          )}
        </div>
      )}

      {/* Mode: global */}
      {mode === 'global' && (
        <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', marginBottom: '0.75rem' }}>
            Analisa ~30 cidades do mundo e gera um ranking personalizado para o seu mapa natal.
            O processamento leva cerca de 30–60 segundos.
          </p>
          <button
            className="btn-primary"
            onClick={handleGlobalRanking}
            style={{ padding: '0.75rem 2rem', fontSize: '0.9rem' }}
          >
            🌐 Gerar Ranking Global
          </button>
        </div>
      )}

      {/* Error */}
      {error && error !== 'GPS_DENIED' && (
        <div style={{
          padding: '0.875rem 1rem', marginBottom: '1rem',
          backgroundColor: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 'var(--radius-md)',
          color: '#fca5a5', fontSize: 'var(--text-label)',
        }}>
          ⚠ {error}
        </div>
      )}

      {/* GPS permission denied, rich help card */}
      {error === 'GPS_DENIED' && (
        <div style={{
          padding: '1rem', marginBottom: '1rem',
          backgroundColor: 'rgba(239,68,68,0.06)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-md)',
        }}>
          <p style={{ color: '#fca5a5', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            📍 Acesso à localização bloqueado
          </p>
          <p style={{ color: '#C5B99A', fontSize: '0.8rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
            Seu navegador negou o acesso ao GPS. Para liberar:
          </p>
          <div style={{ fontSize: '0.78rem', color: '#C5B99A', lineHeight: 1.7 }}>
            <p style={{ margin: '0 0 0.25rem' }}><strong style={{ color: '#F5F0E8' }}>iPhone/iPad (Safari):</strong></p>
            <p style={{ margin: '0 0 0.5rem', paddingLeft: '0.75rem' }}>
              Ajustes → Safari → Localização → Permitir &nbsp;·&nbsp; ou &nbsp;·&nbsp;
              Ajustes → Professya → Localização → Ao usar
            </p>
            <p style={{ margin: '0 0 0.25rem' }}><strong style={{ color: '#F5F0E8' }}>Android (Chrome):</strong></p>
            <p style={{ margin: 0, paddingLeft: '0.75rem' }}>
              Toque no 🔒 na barra de endereço → Permissões → Localização → Permitir
            </p>
          </div>
          <button
            onClick={() => { setError(null); setGpsStatus('idle'); setMode('specific'); }}
            style={{
              marginTop: '0.75rem', padding: '0.5rem 1rem',
              borderRadius: '999px', border: '1px solid rgba(31,78,95,0.35)',
              background: 'transparent', color: '#D4A017', fontSize: '0.8rem', cursor: 'pointer',
            }}
          >
            Digitar cidade manualmente →
          </button>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 600 }}>
            Análises recentes
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {history.map((item, i) => (
              <div
                key={item.id ?? i}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-md)',
                  transition: 'border-color 0.15s',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--border-gold)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-default)')}
              >
                {/* Main clickable area */}
                <button
                  onClick={() => { setQuery(item.locationName); setResult(item); }}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.75rem 1rem', background: 'none', border: 'none',
                    cursor: 'pointer', textAlign: 'left', minWidth: 0,
                  }}
                >
                  <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{item.flag}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.locationName}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{item.country}</p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span className={scoreColor(item.overallScore)} style={{ fontWeight: 700, fontSize: '1rem' }}>
                      {item.overallScore}
                    </span>
                    <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>/100</span>
                  </div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '1rem', flexShrink: 0 }}>›</span>
                </button>

                {/* Delete button */}
                {item.id && (
                  <button
                    onClick={e => handleDeleteAnalysis(e, item.id!)}
                    disabled={deletingId === item.id}
                    title="Remover desta lista"
                    style={{
                      flexShrink: 0, padding: '0.75rem 0.75rem',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: deletingId === item.id ? 'var(--text-muted)' : 'rgba(239,68,68,0.5)',
                      fontSize: '1rem', borderLeft: '1px solid var(--border-subtle)',
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => { if (!deletingId) (e.currentTarget as HTMLButtonElement).style.color = 'rgb(239,68,68)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(239,68,68,0.5)'; }}
                  >
                    {deletingId === item.id ? '…' : '🗑'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
